import { useAtomValue } from "jotai";
import { Node, NodeProps } from "@xyflow/react";
import classNames from "classnames";
import { Colors } from "@zapier/design-system";
import { isRemirrorJSONEmpty } from "@zapier/rich-text-editor";
import { DEFAULT_NODE_COLOR } from "@zapier/canvas-constants";
import {
  DefaultNodeData,
  isPublicAtom,
  isValidUUID,
  togglesAtomResettable,
  isTemplatePageAtom,
  isViewOnlyModeAtom,
} from "@zapier/canvas-state";

import { getItemsForStepSummary } from "../../../../utils/getItemsForStepSummary";
import { determineAssetType } from "../../../../utils/determineAssetType";
import { isAssetNode } from "../../../../utils/isAssetNode";
import { lightenColor } from "../../../../utils/lightenColor";
import { AssetHeading } from "../../../AssetHeading";
import { StepTitle } from "../../../StepTitle";
import { StepSummary } from "../../../StepSummary";
import { TimeSpent } from "../../../TimeMetrics";
import { StepUsage } from "../../../StepUsage";
import { StepStatusRow } from "../../../StepStatusRow";
import { StepDetails } from "../../../StepDetails";
import { FormsSubmissionsCount } from "../../../FormsSubmissionsCount";
import { FlexRow } from "../../../FlexRow";
import { DraftTag } from "../../DraftTag/DraftTag";
import { AssetNodeDeterminer } from "../AssetNodeSkeleton/AssetNodeDeterminer";

import styles from "./DefaultNode.module.css";

interface DefaultNodeContentProps {
  className: string;
  children?: React.ReactNode;
  isViewOnlyMode?: boolean;
}

export function DefaultNodeContentInner({
  className,
  children,
  ...props
}: NodeProps<Node<DefaultNodeData>> & DefaultNodeContentProps) {
  const { data, id, selected } = props;
  const { label, services, color = DEFAULT_NODE_COLOR, actors } = data;
  const isAssetNodeType = isAssetNode(data);
  const toggles = useAtomValue(togglesAtomResettable);
  const isDraftNode = !isValidUUID(id);
  const isPublic = useAtomValue(isPublicAtom);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);

  const hasNote = !isRemirrorJSONEmpty(data.notesJson);

  const dynamicStyles = {
    "--border-color": color,
    "--background-color": !toggles.processAssetTypeNodes
      ? Colors.BackgroundWeaker
      : color === DEFAULT_NODE_COLOR
        ? Colors.BackgroundWeaker
        : lightenColor(color!, 0.2, 1.5),
  };

  const stepSummaryItems = getItemsForStepSummary({
    apps: services,
    actors,
    hasNote: Boolean(hasNote),
  });
  // Currently only supporting details for table nodes and interface nodes
  const expandable =
    (!isPublic &&
      (Boolean(data.tableId) ||
        Boolean(data.projectId) ||
        Boolean(data.pageId))) ||
    Boolean(data.proposalId);

  const canNodeGetAiSuggestion =
    toggles.showSmartNodeSuggest &&
    !data.app &&
    !data.projectId &&
    !data.pageId &&
    !data.chatbotId;
  const isTemplatePage = useAtomValue(isTemplatePageAtom);

  const shouldShowDraftState =
    isDraftNode && toggles.aiCanvasComposer && !isTemplatePage;
  return (
    <div
      className={classNames(
        styles.wrapper,
        {
          [styles.wrapperNew]: toggles.processAssetTypeNodes,
          [styles.assetNode]: isAssetNodeType,
          [styles.draftNode]: shouldShowDraftState,
          "asset-node": isAssetNodeType,
          "default-node": !isAssetNodeType,
        },
        className,
      )}
      style={dynamicStyles as React.CSSProperties}
    >
      {shouldShowDraftState && <DraftTag />}
      {children}
      <StepStatusRow
        nodeId={id}
        assetType={determineAssetType(data.services, "default")}
        zapId={undefined}
        interfaceProjectId={data.projectId}
        interfacePageId={data.pageId}
        tableId={data.tableId}
        chatbotId={data.chatbotId}
      />
      <StepTitle
        nodeId={id}
        label={label}
        isSelected={Boolean(selected)}
        canNodeGetAiSuggestion={canNodeGetAiSuggestion}
        isViewOnlyMode={isViewOnlyMode}
      />
      {!isPublic && (
        <StepUsage tableId={data.tableId} chatbotId={data.chatbotId} />
      )}
      <FlexRow>
        <FormsSubmissionsCount
          nodeId={id}
          interfaceProjectId={data.projectId}
          interfacePageId={data.pageId}
        />
        <TimeSpent timeSpent={data.timeSpent} />
      </FlexRow>
      <AssetHeading
        nodeId={id}
        assetType={determineAssetType(services, "default")}
        tableId={data.tableId}
        interfaceProjectId={data.projectId}
        interfacePageId={data.pageId}
        chatbotId={data.chatbotId}
      />
      {stepSummaryItems.length > 0 && (
        <StepSummary
          items={stepSummaryItems}
          isAssetNodeType={isAssetNodeType}
        />
      )}
      {expandable && (
        <StepDetails
          nodeId={id}
          proposalId={data.proposalId}
          tableId={data.tableId}
          interfacePageId={data.pageId}
          interfaceProjectId={data.projectId}
        />
      )}
    </div>
  );
}

/* This is a presentational component - please keep all RF smarts in the outer DefaultNode component
 * We use this to render a node outside of RF.
 * */
export function DefaultNodeContent(
  props: NodeProps<Node<DefaultNodeData>> & DefaultNodeContentProps,
) {
  const { children, ...rest } = props;
  const toggles = useAtomValue(togglesAtomResettable);
  if (toggles.processAssetTypeNodes) {
    return <AssetNodeDeterminer {...rest} />;
  }
  return <DefaultNodeContentInner {...props} />;
}
